import type {FC, SVGProps} from 'react';

import AdvocateHealthLogo from './logos/advocate-health.svg';
import AetnaCVSHealthLogo from './logos/aetna-cvs-health.svg';
import CircleEngageNewLogo from './logos/circle-engage-new.svg';
import CleverBenefitsLogo from './logos/clever-benefits.svg';
import EvernorthLogo from './logos/evernorth.svg';
import HeadspaceLogo from './logos/headspace.svg';
import MeQLogo from './logos/me-q.svg';
import OptumLogo from './logos/optum.svg';
import RflLogo from './logos/rfl.svg';
import RghDarkLogo from './logos/rgh-dark.svg';
import RghLogo from './logos/rgh.svg';
import DummyLogo from './logos/your-company.svg';

export type Partner = {
  color: string;
  colorifyLogo: boolean;
  id: PartnerId;
  logo: FC<SVGProps<SVGElement>>;
  logoColor?: string;
  /** Variant for dark surfaces; partners without one reuse `logo`. */
  logoDark?: FC<SVGProps<SVGElement>>;
  name: string;
  shortName: string;
};

type PartnerId =
  | 'advocatehealth'
  | 'aetnacvshealth'
  | 'circle_engage'
  | 'circle_engage_crm'
  | 'cleverbenefits'
  | 'evernorth'
  | 'headspace'
  | 'meq'
  | 'optum'
  | 'rfl'
  | 'rgh_wellbeing'
  | 'your_company';

export const isPartner = (partner: string, partner_ids: PartnerId[]) => {
  let verdict = false;

  for (const partner_id of partner_ids) {
    if (partner.includes(partner_id) && !verdict) {
      verdict = true;
      break;
    }
  }

  return verdict;
};

export const isCircleEngage = (partner: string) =>
  isPartner(partner, ['circle_engage', 'circle_engage_crm']);

const PARTNERS: Map<PartnerId, Partner> = new Map([
  [
    'advocatehealth',
    {
      color: '#46749b',
      colorifyLogo: true,
      id: 'advocatehealth',
      logo: AdvocateHealthLogo,
      name: 'Advocate Health',
      shortName: 'AdvH',
    },
  ],
  [
    'aetnacvshealth',
    {
      color: '#8932AF',
      colorifyLogo: true,
      id: 'aetnacvshealth',
      logo: AetnaCVSHealthLogo,
      name: 'Aetna CVS Health',
      shortName: 'ACVSH',
    },
  ],
  [
    'circle_engage',
    {
      color: '#1C699A',
      colorifyLogo: true,
      id: 'circle_engage',
      logo: CircleEngageNewLogo,
      logoColor: '#0C0C47',
      name: 'Circle Engage',
      shortName: 'CE',
    },
  ],
  [
    'circle_engage_crm',
    {
      color: '#1C699A',
      colorifyLogo: true,
      id: 'circle_engage',
      logo: CircleEngageNewLogo,
      logoColor: '#0C0C47',
      name: 'Circle Engage',
      shortName: 'CE',
    },
  ],

  [
    'cleverbenefits',
    {
      color: '#dd4124',
      colorifyLogo: true,
      id: 'cleverbenefits',
      logo: CleverBenefitsLogo,
      name: 'Clever Benefits',
      shortName: 'clrbfts',
    },
  ],
  [
    'evernorth',
    {
      color: '#265B65',
      colorifyLogo: true,
      id: 'evernorth',
      logo: EvernorthLogo,
      name: 'Evernorth',
      shortName: 'EvN',
    },
  ],
  [
    'headspace',
    {
      color: '#EF853B',
      colorifyLogo: false,
      id: 'headspace',
      logo: HeadspaceLogo,
      name: 'headspace',
      shortName: 'hdspace',
    },
  ],
  [
    'meq',
    {
      color: '#e74e2e',
      colorifyLogo: true,
      id: 'meq',
      logo: MeQLogo,
      name: 'meQ',
      shortName: 'meq',
    },
  ],
  [
    'optum',
    {
      color: '#ff612b',
      colorifyLogo: true,
      id: 'optum',
      logo: OptumLogo,
      name: 'Optum',
      shortName: 'opt',
    },
  ],
  [
    'rfl',
    {
      color: '#0a4b8c',
      colorifyLogo: true,
      id: 'rfl',
      logo: RflLogo,
      name: 'Resources for Living',
      shortName: 'rfl',
    },
  ],
  [
    'rgh_wellbeing',
    {
      color: '#255A83',
      colorifyLogo: false,
      id: 'rgh_wellbeing',
      logo: RghLogo,
      logoDark: RghDarkLogo,
      name: 'Roane General Hospital',
      shortName: 'rgh',
    },
  ],
  [
    'your_company',
    {
      color: '#767d5e',
      colorifyLogo: true,
      id: 'your_company',
      logo: DummyLogo,
      name: 'Your Company',
      shortName: 'YrC',
    },
  ],
]);

export const getPartner = (id: PartnerId | (string & {})) => {
  return PARTNERS.get(id as PartnerId);
};
